import { Component, ComponentType } from '@Types/component.types';
import { CoverageEntry } from '@Types/riskCoverage.types';

// Mirrors COVERAGE_INELIGIBLE_TYPES in saas_risk_management's
// src/util/coverageEligibility.ts — both sides are computed for every
// asset, so "missing data" cannot apply to them. Keep these two lists in
// sync; the Review Focus test above pins the literal strings so drift
// fails loudly instead of silently.
export const COVERAGE_SKIP_TYPES: ComponentType[] = [
	ComponentType.SpatialProximity,
	ComponentType.SpatialAttribute,
];

// Fails closed for undefined types to match the backend mirror — we only show
// a coverage pill when we know the type and it's eligible.
export const isCoverageEligible = (type: ComponentType | undefined): boolean =>
	!!type && !COVERAGE_SKIP_TYPES.includes(type);

// Coverage below this is "thin": most assets fall into the "--" bucket.
export const LOW_COVERAGE_PCT = 35;
// A selected component weighted at or above this (% within its category) is
// "heavy". Heavy weight on thin data is the setup-time risk worth flagging.
export const HEAVY_WEIGHT_PCT = 30;

// Whether a coverage pill/guidance should be shown at all. Gated by component
// type and data only — never by whether the component came from a template.
// Hidden for "100% of nothing" (no assets in scope) and for not-yet-computed
// coverage (pct null), which is distinct from a confirmed 0%.
export const hasCoverageToShow = (
	type: ComponentType | undefined,
	entry: CoverageEntry | undefined,
): boolean =>
	isCoverageEligible(type) &&
	entry !== undefined &&
	entry.pct !== null &&
	entry.assets > 0;

export const isHighWeightLowCoverage = (
	weighting: number,
	selected: boolean,
	pct: number | null | undefined,
): boolean =>
	selected &&
	weighting >= HEAVY_WEIGHT_PCT &&
	pct !== null &&
	pct !== undefined &&
	pct < LOW_COVERAGE_PCT;

// The fields that decide which data a component reads. Coverage is computed
// server-side from the *saved* config, so it only describes the component the
// user is looking at while these still match the saved copy.
const SOURCE_FIELDS = [
	'type',
	'field',
	'fieldAlias',
	'simulationName',
	'simulationUploadId',
	'uploadId',
	'taskName',
	'layer',
	'failureDefinitionId',
	'deteriorationModelId',
] as const;

export const hasSameCoverageSource = (
	edited: Component,
	saved: Component | undefined,
): boolean =>
	saved !== undefined &&
	SOURCE_FIELDS.every(field => edited[field] === saved[field]);
